import React, { useEffect, useState, useRef, useMemo } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import axios from 'axios';
import EditIcon from '@material-ui/icons/Edit';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import DeleteIcon from '@material-ui/icons/Delete';
import './PODashboard.css';

// ── Default Reports (Direct Query IDs) ──────────────────
const DEFAULT_REPORTS = [
  { id: '39d04ca6-8673-40f5-b6b6-b25c13bd069d', title: 'Plot 1: OTIF', plotType: 'otif', hasOutOfRange: true },
  { id: 'ff09dc76-3aca-4f14-a955-ee422085336e', title: 'Plot 2: Days Late / Early vs # PO Histogram', plotType: 'delivery', hasOutOfRange: true },
  { id: '26222cfb-9562-405b-afcd-09f54ea2f325', title: 'Plot 3: Days From PO Creation To GRN/IBD Generation', plotType: 'poc', hasOutOfRange: true },
  { id: '0c7f0422-5975-4da2-9b16-cabd54f7f002', title: 'Plot 4: GRN/IBD Processing Efficiency',                plotType: 'grn_intervention',   hasOutOfRange: true,  hasSummary: true, hasDataTable: true },
   { id: 'fe471f72-46b3-4c70-b8f9-03285b9a7ce9', title: 'Plot 5: Days Late From Original Stat Date',           plotType: 'days_late_orig',     hasOutOfRange: true,  hasSummary: true, hasDataTable: true },
  { id: '5cbff698-bdc7-4f27-9568-32b125a31301', title: 'Plot 6: GRNs/IBDs Created Per Week',                  plotType: 'grn_created',        hasOutOfRange: false, hasSummary: true, hasDataTable: true },
  { id: 'e337c0be-30ff-4f0e-bdab-bcfdf2e87194', title: 'Plot 7: Days from CMO Document Receipt to GRN/IBD Generation',       plotType: 'receipt_to_posting', hasOutOfRange: true,  hasSummary: true, hasDataTable: true },
   { id: '4d5e5ff7-1fa2-49a6-8e05-09a328b95c6e', title: 'Plot 8: Days For BMS To Accept/Reject CMO Proposals', plotType: 'bms_response',       hasOutOfRange: true,  hasSummary: true, hasDataTable: true },
   { id: '70c4451d-0162-4d1b-a81a-d94c4653f85c', title: 'Plot 9: Days For CMO To Accept/Reject BMS Proposals', plotType: 'cmo_response',       hasOutOfRange: true,  hasSummary: true, hasDataTable: true },
  { id: '324c3761-d68e-49e8-908a-cc504143103e', title: 'Plot 10: Days from PO Creation to CMO Document Receipt',  plotType: 'po_to_receipt',      hasOutOfRange: true,  hasSummary: true, hasDataTable: true },
  { id: 'e4c14833-16db-4542-9308-d54edd90cb8f', title: 'Plot 11: Auto-Accepted POs',plotType: 'auto_accepted', hasOutOfRange: true,  hasSummary: true, oorCheckboxLabel: 'View Auto-Accepted POs', oorTitle: 'Auto-Accepted POs' },
];

// ── Per-plot filter targets (table + column names per dataset) ──
const PLOT_FILTER_TARGETS = {
  otif: {
    table: 'public vw_otif_data',
    dateCol: 'posting_date',
    cmoCol: 'cmo',
    brandCol: 'brand',
    productCol: 'product_type',
    productCodeCol: 'product_code',
  },
  delivery: {
    table: 'public vw_days_late_data',
    dateCol: 'posting_date',
    cmoCol: 'cmo',
    brandCol: 'brand',
    productCol: 'product_type',
    productCodeCol: 'product_code',
  },
  poc: {
    table: 'public vw_poc_data',
    dateCol: 'posting_date',
    cmoCol: 'cmo',
    brandCol: 'brand',
    productCol: 'product_type',
    productCodeCol: 'product_code',
  },
  // schedule_lines uses camelCase column names as imported from PostgreSQL
  schedule_lines: {
    table: 'schedule_lines',
    dateCol: 'posting_date',
    cmoCol: 'vendorName',
    brandCol: 'brand',
    productCol: 'productType',
    productCodeCol: 'fgMaterialCode',
  },
};

// All known dataset tables — used to fan out filters for custom plots.
// Power BI silently ignores filters that target tables/columns not present in the visual.
const ALL_FILTER_TARGETS = Object.values(PLOT_FILTER_TARGETS);

const DURATION_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_6_months', label: 'Last 6 months' },
  { value: 'last_12_months', label: 'Last 12 months' },
];

const XAXIS_OPTIONS = [{ value: 'Duration', label: 'Duration' }];

const YAXIS_OPTIONS_MAP = {
  otif: [{ value: 'OTIF %', label: 'OTIF %' }],
  delivery: [{ value: 'Number of POs', label: 'Number of POs' }],
  poc: [{ value: 'Number of POs', label: 'Number of POs' }],
  // For custom plots the Y-axis is whatever the user dragged in Power BI.
  // Show a single "Custom" entry so the dropdown is visible for UI parity.
  custom: [{ value: 'Custom', label: 'Custom' }],
};

// ── Calendar month date range calculator ────────────────
const getDurationRange = (durationKey) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  switch (durationKey) {
    case 'this_month': {
      const from = new Date(year, month, 1, 0, 0, 0);
      const to = new Date(year, month + 1, 0, 23, 59, 59);
      return { from, to };
    }
    case 'last_month': {
      const from = new Date(year, month - 1, 1, 0, 0, 0);
      const to = new Date(year, month, 0, 23, 59, 59);
      return { from, to };
    }
    case 'last_6_months': {
      const from = new Date(year, month - 5, 1, 0, 0, 0);
      const to = new Date(year, month + 1, 0, 23, 59, 59);
      return { from, to };
    }
    case 'last_12_months': {
      const from = new Date(year, month - 11, 1, 0, 0, 0);
      const to = new Date(year, month + 1, 0, 23, 59, 59);
      return { from, to };
    }
    default:
      return null;
  }
};

// ── Success Modal ───────────────────────────────────────
const SuccessModal = ({ message, onClose }) => (
  <div className="modal-overlay">
    <div className="modal-box success-modal">
      <div className="success-icon">
        <svg viewBox="0 0 52 52" width="64" height="64">
          <circle cx="26" cy="26" r="25" fill="none" stroke="#2ecc71" strokeWidth="2"/>
          <path fill="none" stroke="#2ecc71" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" d="M14 27l8 8 16-16"/>
        </svg>
      </div>
      <h3 className="success-title">{message}</h3>
      <button className="success-ok-btn" onClick={onClose}>Ok</button>
    </div>
  </div>
);

// ── Toast ───────────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`toast toast--${type}`}>
      <span className="toast__icon">
        {type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}
      </span>
      <span className="toast__message">{message}</span>
      <button className="toast__close" onClick={onClose}>×</button>
    </div>
  );
};

// ── Confirm Modal ───────────────────────────────────────
const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div className="modal-overlay">
    <div className="modal-box confirm-modal">
      <p className="confirm-modal__message">{message}</p>
      <div className="confirm-modal__actions">
        <button className="btn btn--outline" onClick={onCancel}>Cancel</button>
        <button className="btn btn--dark" onClick={onConfirm}>Delete</button>
      </div>
    </div>
  </div>
);

// ── Rename Modal ────────────────────────────────────────
const RenameModal = ({ currentName, onConfirm, onCancel }) => {
  const [name, setName] = useState(currentName || '');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Plot name cannot be empty');
      return;
    }
    if (trimmed === currentName) {
      onCancel();
      return;
    }
    onConfirm(trimmed);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box rename-modal">
        <div className="rename-modal__header">
          <span className="rename-modal__title">Rename Plot</span>
          <button className="modal-close-btn" onClick={onCancel}>✕</button>
        </div>
        <div className="rename-modal__body">
          <label className="rename-modal__label">Plot Name</label>
          <input
            ref={inputRef}
            type="text"
            className={`rename-modal__input ${error ? 'rename-modal__input--error' : ''}`}
            value={name}
            onChange={e => { setName(e.target.value); setError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            placeholder="Enter new name"
          />
          {error && <span className="rename-modal__error">{error}</span>}
        </div>
        <div className="rename-modal__actions">
          <button className="btn btn--outline" onClick={onCancel}>Cancel</button>
          <button className="btn btn--dark" onClick={handleSubmit}>Save</button>
        </div>
      </div>
    </div>
  );
};

// ── Plot Action Menu (Edit / Rename / Delete dropdown) ──
const PlotActionMenu = ({ onEdit, onRename, onDelete }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="plot-action-menu" ref={menuRef}>
      <button
        className="plot-card__menu-btn"
        title="Plot options"
        onClick={e => { e.stopPropagation(); setOpen(!open); }}
      >⋮</button>
      {open && (
        <div className="plot-action-menu__dropdown" onClick={e => e.stopPropagation()}>
          <button className="plot-action-menu__item" onClick={() => { setOpen(false); onEdit(); }}>
            <EditIcon className="plot-action-menu__icon" fontSize="small" />
            <span>Edit Chart</span>
          </button>
          <button className="plot-action-menu__item" onClick={() => { setOpen(false); onRename(); }}>
            <BorderColorIcon className="plot-action-menu__icon" fontSize="small" />
            <span>Rename</span>
          </button>
          <button className="plot-action-menu__item plot-action-menu__item--danger" onClick={() => { setOpen(false); onDelete(); }}>
            <DeleteIcon className="plot-action-menu__icon" fontSize="small" />
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ── Plot Card ───────────────────────────────────────────
// Normalize a column name: strip spaces, underscores, hyphens, then lowercase.
// "Product Type", "product_type", "productType", "PRODUCT-TYPE" → "producttype".
const normalizeCol = (s) => (s || '').toLowerCase().replace(/[\s_\-]/g, '');

// Maps a slicer's underlying column name to the matching side-panel setting.
// Returns the filter array to push to setSlicerState, or null to leave it untouched.
const slicerFiltersForColumn = (target, settings, selectedProduct) => {
  const col = normalizeCol(target?.column);
  if (!col) return null;

  // CMO — vw_*_data uses `cmo`, schedule_lines uses `vendorName`
  if (col === 'cmo' || col === 'vendorname') {
    if (settings.cmo && settings.cmo !== 'All') {
      return [{
        $schema: 'http://powerbi.com/product/schema#basic',
        target, operator: 'In', values: [settings.cmo], filterType: 1,
      }];
    }
    return [];
  }
  // Brand
  if (col === 'brand') {
    if (settings.brand && settings.brand !== 'All') {
      return [{
        $schema: 'http://powerbi.com/product/schema#basic',
        target, operator: 'In', values: [settings.brand], filterType: 1,
      }];
    }
    return [];
  }
  // Product Type — vw_* uses `product_type`, schedule_lines uses `productType`
  if (col === 'producttype') {
    if (settings.productType && settings.productType !== 'Multiple Selection') {
      return [{
        $schema: 'http://powerbi.com/product/schema#basic',
        target, operator: 'In', values: [settings.productType], filterType: 1,
      }];
    }
    return [];
  }
  // Product Code — synced with the top-bar product search dropdown
  if (col === 'productcode' || col === 'fgmaterialcode') {
    if (selectedProduct?.code) {
      return [{
        $schema: 'http://powerbi.com/product/schema#basic',
        target, operator: 'In', values: [selectedProduct.code], filterType: 1,
      }];
    }
    return [];
  }
  // Duration (date column)
  if (col === 'postingdate' || col === 'date') {
    const range = getDurationRange(settings.duration);
    if (range) {
      return [{
        $schema: 'http://powerbi.com/product/schema#advanced',
        target, logicalOperator: 'And',
        conditions: [
          { operator: 'GreaterThanOrEqual', value: range.from.toISOString() },
          { operator: 'LessThanOrEqual',    value: range.to.toISOString() },
        ],
        filterType: 0,
      }];
    }
    return [];
  }
  return null;  // unrelated slicer — leave alone
};

// For custom plots cloned from the template, filtering happens via the hidden
// sync-enabled slicers on Page 2. Walk all pages, find each slicer, and set
// its state from the side-panel settings (+ top-bar product search).
const applyFiltersViaSlicers = async (reportInstance, settings, selectedProduct) => {
  try {
    const pages = await reportInstance.getPages();
    for (const page of pages) {
      let visuals;
      try { visuals = await page.getVisuals(); }
      catch { continue; }
      for (const slicer of visuals.filter(v => v.type === 'slicer')) {
        try {
          const state = await slicer.getSlicerState();
          const target = state?.targets?.[0];
          if (!target) continue;
          const filters = slicerFiltersForColumn(target, settings, selectedProduct);
          if (filters !== null) {
            await slicer.setSlicerState({ filters });
          }
        } catch (err) {
          console.warn('[PowerBI] slicer state update failed', err);
        }
      }
    }
  } catch (err) {
    console.error('[PowerBI] applyFiltersViaSlicers error', err);
  }
};

const PlotCard = ({ report, onCheckChange, checked, isSelected, onSelect, onReportLoaded, activeFilters, activeSettings, activeDuration, selectedProduct, onEdit, onRename, onDelete, isCustom, refreshKey }) => {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [reportInstance, setReportInstance] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchToken();
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const fetchToken = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get(`/powerbi/embed-token/${report.id}`);
      if (!mountedRef.current) return;
      const { token, embedUrl } = data.data;
      setEmbedConfig({
        type: 'report', id: report.id, embedUrl,
        accessToken: token, tokenType: models.TokenType.Embed,
        settings: {
          panes: {
            filters: { expanded: false, visible: false },
            pageNavigation: { visible: false },
          },
          background: models.BackgroundType.Transparent,
          layoutType: models.LayoutType.Custom,
          customLayout: { displayOption: models.DisplayOption.FitToPage },
          bars: { actionBar: { visible: false } },
          visualSettings: { visualHeaders: [{ settings: { visible: false } }] },
        },
      });
      setLoading(false);
      setTimeout(() => { if (mountedRef.current) fetchToken(); }, 55 * 60 * 1000);
    } catch (err) {
      console.error('Embed token error:', err);
      if (mountedRef.current) { setError('Failed to load report'); setLoading(false); }
    }
  };

  useEffect(() => {
    if (!reportInstance || !mountedRef.current) return;
    const apply = async () => {
      try {
        if (isCustom) {
          // Custom plots: drive the hidden slicers cloned from the template.
          // Sync-slicers will propagate the filter to the visual on Page 1.
          await applyFiltersViaSlicers(reportInstance, activeSettings || {}, selectedProduct);
          return;
        }

        // Default plots: switch page (Daily/Monthly) then apply report-level filters.
        const isDailyView = activeDuration === 'this_month' || activeDuration === 'last_month';
        const targetPageName = isDailyView ? 'Daily View' : 'Monthly View';
        const pages = await reportInstance.getPages();
        const targetPage = pages.find(p => p.displayName === targetPageName);
        if (!targetPage) {
          console.warn(`Page "${targetPageName}" not found in report ${report.id}; skipping page switch.`);
        } else if (!targetPage.isActive) {
          await targetPage.setActive();
        }

        if (!activeFilters || activeFilters.length === 0) {
          await reportInstance.removeFilters();
        } else {
          await reportInstance.setFilters(activeFilters);
        }
      } catch (err) {
        console.error('apply filter error', report.id, err);
      }
    };
    apply();
  }, [activeFilters, activeSettings, activeDuration, selectedProduct, reportInstance, isCustom]);

  return (
    <div
      className={`plot-card ${expanded ? 'plot-card--expanded' : ''} ${isSelected ? 'plot-card--selected' : ''} ${isCustom ? 'plot-card--custom' : ''}`}
      onClick={() => !expanded && onSelect(report.id)}
    >
      <div className="plot-card__header">
        <span className="plot-card__title">{report.title}</span>
        <div className="plot-card__header-actions">
          {expanded && (
            <div className="plot-card__zoom-controls">
              <button className="zoom-btn" title="Zoom out"
                onClick={e => { e.stopPropagation(); setZoom(z => Math.max(0.5, +(z - 0.1).toFixed(1))); }}>−</button>
              <span className="zoom-level">{Math.round(zoom * 100)}%</span>
              <button className="zoom-btn" title="Zoom in"
                onClick={e => { e.stopPropagation(); setZoom(z => Math.min(2, +(z + 0.1).toFixed(1))); }}>+</button>
            </div>
          )}
          {isCustom && (onEdit || onRename || onDelete) && (
            <PlotActionMenu
              onEdit={() => onEdit && onEdit(report)}
              onRename={() => onRename && onRename(report)}
              onDelete={() => onDelete && onDelete(report)}
            />
          )}
          <button className="plot-card__expand"
            title={expanded ? 'Minimize' : 'Enlarge'}
            onClick={e => { e.stopPropagation(); setExpanded(!expanded); if (expanded) setZoom(1); }}>
            {expanded ? '✕' : '⤢'}
          </button>
        </div>
      </div>
      <div className="plot-card__body" style={expanded ? { zoom } : {}}>
        {loading && <div className="plot-card__loading"><div className="spinner" /><p>Loading...</p></div>}
        {error && <div className="plot-card__error"><p>{error}</p><button onClick={fetchToken} className="retry-btn">Retry</button></div>}
        {!loading && !error && embedConfig && (
          <PowerBIEmbed
            embedConfig={embedConfig}
            cssClassName="plot-card__embed"
            getEmbeddedComponent={e => {
              setReportInstance(e);
              if (onReportLoaded) onReportLoaded(report.id, e);
            }}
          />
        )}
      </div>
      {report.hasOutOfRange && (
        <div className="plot-card__footer">
          <label className="plot-card__checkbox" onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={checked} onChange={e => onCheckChange(e.target.checked)} />
            Show out of range POs or Batches
          </label>
        </div>
      )}
    </div>
  );
};

// ── Create Mode Plot (clone template → edit cloned report → name + save) ──
// On mount, immediately clones the template report (which has the 4 hidden
// CMO/Brand/ProductType/Duration slicers) and opens it in edit mode so user
// can drag their visual onto the empty page. On Save Plot, renames the
// clone to the user's chosen name. On Cancel, deletes the orphaned clone.
const CreateModePlot = ({ onCancel, onSaved }) => {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plotName, setPlotName] = useState('');
  const [nameError, setNameError] = useState('');
  const [reportInstance, setReportInstance] = useState(null);
  const [clonedPlot, setClonedPlot] = useState(null); // { reportId, plotId, placeholderName }
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    initialize();
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialize = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1) Clone the template with a placeholder name — gives us a new
      //    report ID that already has the hidden slicers configured.
      const placeholder = `Untitled Plot ${Date.now()}`;
      const cloneRes = await axios.post('/powerbi/custom-plots/create-from-template', {
        plotName: placeholder,
      });
      if (!cloneRes.data?.status) {
        throw new Error(cloneRes.data?.message || 'Failed to clone template');
      }
      const { reportId, plotId } = cloneRes.data.data;
      if (!mountedRef.current) return;
      setClonedPlot({ reportId, plotId, placeholderName: placeholder });

      // 2) Fetch an edit-mode token for the cloned report.
      const tokenRes = await axios.get(`/powerbi/edit-mode-token/${reportId}`);
      if (!mountedRef.current) return;
      const { token, embedUrl } = tokenRes.data.data;

      setEmbedConfig({
        type: 'report',
        id: reportId,
        embedUrl,
        accessToken: token,
        tokenType: models.TokenType.Embed,
        viewMode: models.ViewMode.Edit,
        permissions: models.Permissions.All,
        settings: {
          panes: {
            filters: { expanded: true, visible: true },
            pageNavigation: { visible: false },
          },
          background: models.BackgroundType.Transparent,
          bars: { actionBar: { visible: true } },
        },
      });
      setLoading(false);
    } catch (err) {
      console.error('Clone + open template error:', err);
      if (mountedRef.current) {
        const msg = err.response?.data?.message || err.message || 'Unknown error';
        setError(`Failed to initialize new plot: ${msg}`);
        setLoading(false);
      }
    }
  };

  const handleSavePlot = async () => {
    setNameError('');

    if (!plotName.trim()) {
      setNameError('Please enter a name for your plot');
      return;
    }
    if (!clonedPlot) {
      setError('Plot was not initialized. Please cancel and try again.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // 1) Save visual changes inside Power BI (whatever the user dragged onto Page 1).
      if (reportInstance && typeof reportInstance.save === 'function') {
        try { await reportInstance.save(); }
        catch (e) { console.warn('reportInstance.save() failed (continuing):', e); }
      }

      // 2) Rename the cloned plot in our DB (and ideally the Power BI report too).
      const renameRes = await axios.put(
        `/powerbi/custom-plots/${clonedPlot.plotId}/rename`,
        { plotName: plotName.trim() }
      );
      if (!renameRes.data?.status) {
        throw new Error(renameRes.data?.message || 'Rename failed');
      }

      onSaved({
        id: clonedPlot.reportId,
        title: plotName.trim(),
        plotId: clonedPlot.plotId,
      });
    } catch (err) {
      console.error('Save plot error:', err);
      const msg = err.response?.data?.message || err.message || 'Unknown error';
      setError(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  // If the user cancels after the clone, delete the orphaned report so it
  // doesn't clutter the workspace and DB.
  const handleCancelAndCleanup = async () => {
    if (clonedPlot?.plotId) {
      try {
        await axios.delete(`/powerbi/custom-plots/${clonedPlot.plotId}`);
      } catch (e) {
        console.warn('Cleanup of cancelled plot failed (orphan may remain):', e);
      }
    }
    onCancel();
  };

  return (
    <div className="create-mode-container">
      <div className="create-mode-header">
        <span className="create-mode-title">Build Your Custom Plot</span>
        <span className="create-mode-hint">
          Use Power BI's tools below to drag fields, choose visuals, and add filters.
          Enter a name and click <b>Save Plot</b> when done.
        </span>
      </div>

      {error && (
        <div className="create-mode-banner create-mode-banner--error">
          <span className="banner-icon">⚠</span>
          <span className="banner-text">{error}</span>
          <button className="banner-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="create-mode-body">
        {loading && <div className="create-mode-loading">Preparing your plot canvas...</div>}
        {embedConfig && (
          <PowerBIEmbed
            embedConfig={embedConfig}
            cssClassName="create-mode-iframe"
            getEmbeddedComponent={(instance) => setReportInstance(instance)}
            eventHandlers={new Map([
              ['loaded', () => console.log('New Plot: report loaded')],
              ['rendered', () => console.log('New Plot: report rendered')],
              ['saved', (event) => console.log('New Plot SAVED event:', event?.detail)],
              ['error', (event) => {
                console.error('New Plot iframe error:', event?.detail);
                const detail = event?.detail;
                if (detail?.message === 'SaveReportFailed') {
                  setError('Power BI could not save the report. The embed token may need edit permissions.');
                }
              }],
            ])}
          />
        )}
      </div>

      <div className="create-mode-actions">
        <div className="create-mode-name-input">
          <label className="create-mode-name-label">Plot Name</label>
          <input
            type="text"
            className={`create-mode-name-field ${nameError ? 'create-mode-name-field--error' : ''}`}
            placeholder="Enter a name for your plot"
            value={plotName}
            onChange={e => { setPlotName(e.target.value); setNameError(''); }}
            disabled={saving}
          />
          {nameError && <span className="create-mode-name-error">{nameError}</span>}
        </div>
        <div className="create-mode-buttons">
          <button className="btn btn--outline" onClick={handleCancelAndCleanup} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--dark" onClick={handleSavePlot} disabled={saving || loading || !clonedPlot}>
            {saving ? 'Saving...' : 'Save Plot'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Edit Mode Plot (loads existing report in Power BI Edit mode) ──
const EditModePlot = ({ plotToEdit, onCancel, onSaved }) => {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reportInstance, setReportInstance] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    fetchEditModeToken();
    return () => { mountedRef.current = false; };
  }, []);

  const fetchEditModeToken = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await axios.get(`/powerbi/edit-mode-token/${plotToEdit.id}`);
      if (!mountedRef.current) return;
      const { token, embedUrl, reportId } = data.data;

      setEmbedConfig({
        type: 'report',
        id: reportId,
        embedUrl,
        accessToken: token,
        tokenType: models.TokenType.Embed,
        viewMode: models.ViewMode.Edit,
        permissions: models.Permissions.All,
        settings: {
          panes: {
            filters: { expanded: true, visible: true },
            pageNavigation: { visible: false },
          },
          background: models.BackgroundType.Transparent,
          bars: { actionBar: { visible: true } },
        },
      });
      setLoading(false);
    } catch (err) {
      console.error('Edit Mode token error:', err);
      if (mountedRef.current) {
        setError('Failed to initialize Edit Mode. Please try again.');
        setLoading(false);
      }
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    setError(null);
    try {
      if (reportInstance && typeof reportInstance.save === 'function') {
        await reportInstance.save();
      }
      await new Promise(r => setTimeout(r, 1500));
      onSaved(plotToEdit);
    } catch (err) {
      console.error('Save changes error:', err);
      setError(`Save failed: ${err.message || 'Unknown error'}`);
      setSaving(false);
    }
  };

  return (
    <div className="create-mode-container">
      <div className="create-mode-header">
        <span className="create-mode-title">Edit Plot: {plotToEdit.title}</span>
        <span className="create-mode-hint">
          Make changes using Power BI's tools. Click <b>Save Changes</b> when done.
        </span>
      </div>

      {error && (
        <div className="create-mode-banner create-mode-banner--error">
          <span className="banner-icon">⚠</span>
          <span className="banner-text">{error}</span>
          <button className="banner-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="create-mode-body">
        {loading && <div className="create-mode-loading">Loading Power BI Edit Mode...</div>}
        {embedConfig && (
          <PowerBIEmbed
            embedConfig={embedConfig}
            cssClassName="create-mode-iframe"
            getEmbeddedComponent={(instance) => setReportInstance(instance)}
            eventHandlers={new Map([
              ['loaded', () => console.log('Edit Mode: report loaded')],
              ['rendered', () => console.log('Edit Mode: report rendered')],
              ['saved', (event) => console.log('Edit Mode SAVED event:', event?.detail)],
              ['error', (event) => {
                console.error('Edit Mode iframe error:', event?.detail);
              }],
            ])}
          />
        )}
      </div>

      <div className="create-mode-actions">
        <div className="create-mode-buttons" style={{ marginLeft: 'auto' }}>
          <button className="btn btn--outline" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn--dark" onClick={handleSaveChanges} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Side Panel ──────────────────────────────────────────
const SidePanel = ({ selectedReport, filterValues, plotSettings, setPlotSettings }) => {
  const settings = plotSettings || {};
  const handleChange = (field, value) => setPlotSettings({ ...plotSettings, [field]: value });

  // JS-based sticky: when the panel's natural position scrolls above
  // the top of the page, switch it to position:fixed so it stays visible.
  // (CSS position:sticky is broken here because the parent <main> in the
  //  app shell has overflowX:auto.)
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const [stuckStyle, setStuckStyle] = useState(null);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const STICK_TOP = 140; // px gap below top of viewport when stuck

    const handleScroll = () => {
      const wrap = wrapperRef.current;
      const panel = panelRef.current;
      if (!wrap || !panel) return;
      const wrapRect = wrap.getBoundingClientRect();
      const panelHeight = panel.offsetHeight;
      // Use the .plots-workspace dashed-bordered container as the boundary.
      // The panel must stop sticking exactly at its bottom edge (so it doesn't
      // float over the OOR table or anything else that may appear below).
      const plotsWorkspace = document.querySelector('.plots-workspace');
      const containerBottom = plotsWorkspace
        ? plotsWorkspace.getBoundingClientRect().bottom
        : Infinity;

      if (wrapRect.top >= STICK_TOP) {
        // Not yet scrolled past — natural position in flow.
        setStuckStyle(null);
      } else if (containerBottom < STICK_TOP + panelHeight) {
        // Container's bottom is approaching — anchor panel to container bottom
        // so it scrolls up out of view with the plots area, not over the OOR table.
        setStuckStyle({
          position: 'fixed',
          top: `${containerBottom - panelHeight}px`,
          left: `${wrapRect.left}px`,
          width: `${wrapRect.width}px`,
          zIndex: 50,
        });
      } else {
        // Stuck at top of viewport (with STICK_TOP offset).
        setStuckStyle({
          position: 'fixed',
          top: `${STICK_TOP}px`,
          left: `${wrapRect.left}px`,
          width: `${wrapRect.width}px`,
          zIndex: 50,
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [selectedReport]);

  if (!selectedReport) {
    return (
      <div className="side-panel">
        <div className="side-panel__empty">
          <p>Select a plot to view and modify its settings</p>
        </div>
      </div>
    );
  }

  const yAxisOptions = YAXIS_OPTIONS_MAP[selectedReport.plotType] || [];

  // Reserve the panel's height in flow when stuck, so plots don't reflow.
  const wrapperStyle = stuckStyle && panelRef.current
    ? { width: panelRef.current.offsetWidth, height: panelRef.current.offsetHeight }
    : undefined;

  return (
    <div ref={wrapperRef} className="side-panel-wrapper" style={wrapperStyle}>
    <div ref={panelRef} className="side-panel" style={stuckStyle || undefined}>
      <div className="side-panel__title">
        {selectedReport.title} Filter
      </div>

      <div className="side-panel__section side-panel__section--axis">
        <label className="side-panel__label side-panel__label--xaxis">X-axis</label>
        <div className="side-panel__value">{settings.xAxis || 'Duration'}</div>
      </div>

      {yAxisOptions.length > 0 && (
        <div className="side-panel__section side-panel__section--axis">
          <label className="side-panel__label side-panel__label--yaxis">Y-axis</label>
          <div className="side-panel__value">
            {settings.yAxis || yAxisOptions[0]?.value || ''}
          </div>
        </div>
      )}

      <div className="side-panel__divider" />

      <div className="side-panel__section">
        <label className="side-panel__label side-panel__label--filter">Product Type</label>
        <select className="side-panel__select"
          value={settings.productType || 'Multiple Selection'}
          onChange={e => handleChange('productType', e.target.value)}>
          <option value="Multiple Selection">Multiple Selection</option>
          {(filterValues?.productType || []).map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="side-panel__section">
        <label className="side-panel__label side-panel__label--filter">Brand</label>
        <select className="side-panel__select"
          value={settings.brand || 'All'}
          onChange={e => handleChange('brand', e.target.value)}>
          <option value="All">All</option>
          {(filterValues?.brand || []).map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="side-panel__section">
        <label className="side-panel__label side-panel__label--filter">CMO</label>
        <select className="side-panel__select"
          value={settings.cmo || 'All'}
          onChange={e => handleChange('cmo', e.target.value)}>
          <option value="All">All</option>
          {(filterValues?.vendorName || []).map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      </div>

      <div className="side-panel__section">
        <label className="side-panel__label side-panel__label--filter">Duration</label>
        <select className="side-panel__select"
          value={settings.duration || 'all'}
          onChange={e => handleChange('duration', e.target.value)}>
          {DURATION_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>
    </div>
    </div>
  );
};

// ── Out of Range Table ──────────────────────────────────
const OORTable = ({ report, tableNumber, visible }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (visible && !fetched) {
      setLoading(true);
      axios.get(`/powerbi/out-of-range/${report.id}?plotType=${report.plotType}`)
        .then(res => { setData(res.data.data || []); setFetched(true); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [visible, fetched, report.id, report.plotType]);

  if (!visible) return null;
  const isPoc = report.plotType === 'poc';
  const isOtif = report.plotType === 'otif';
  const yAxisLabel = isOtif ? 'OTIF %' : isPoc ? 'POC Days' : 'Deviation Days';
  const yAxisKey = isOtif ? '[OTIF %]' : isPoc ? '[POC Days]' : '[Deviation Days]';

  return (
    <div className="oor-table-wrapper">
      <div className="oor-table-title">Table {tableNumber}: Out of range POs or Batches for {report.title}</div>
      {loading ? (
        <div className="oor-loading"><div className="spinner" /><p>Loading data...</p></div>
      ) : data.length === 0 ? (
        <div className="oor-empty">No out of range records found</div>
      ) : (
        <div className="oor-table-scroll">
          <table className="oor-table">
            <thead><tr>
              <th>PO Number</th><th>Batch Number</th><th>Stat Date</th><th>SAP PO Qty</th>
              <th>Product Code / Description</th><th>Posting Date</th><th>Posting Qty</th>
              <th>UoM</th><th>{yAxisLabel}</th><th>Category</th>
            </tr></thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  <td>{row['[SA Number]'] || row['[PO Number]'] || '-'}</td>
                  <td>{row['[Batch Number]'] || '-'}</td>
                  <td>{row['[Statistical Date]'] ? new Date(row['[Statistical Date]']).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-').toUpperCase() : '-'}</td>
                  <td>{row['[Target Qty]'] || row['[SAP PO Qty]'] || '-'}</td>
                  <td>{row['[Product Code]'] ? `${row['[Product Code]']} ${row['[Product Description]'] || ''}` : row['[Product Description]'] || '-'}</td>
                  <td>{row['[Delivery Date]'] ? new Date(row['[Delivery Date]']).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-').toUpperCase() : '-'}</td>
                  <td>{row['[Posting Qty]'] || row['[Target Qty]'] || '-'}</td>
                  <td>{row['[UoM]'] || '-'}</td>
                  <td>{row[yAxisKey] || row['[POC Days]'] || row['[Deviation Days]'] || row['[OTIF %]'] || '-'}</td>
                  <td><span className={`oor-badge oor-badge--${(row['[Category]'] || '').toLowerCase()}`}>{row['[Category]']}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ── Download Modal ──────────────────────────────────────
const DownloadModal = ({ reports, onClose, onShowToast }) => {
  const [selected, setSelected] = useState(reports.map(r => r.id));
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (id) => setSelected(p =>
    p.includes(id) ? p.filter(x => x !== id) : [...p, id]
  );

  const handleDownload = async () => {
    if (selected.length === 0) return;
    setDownloading(true);
    setError(null);
    try {
      const selectedReports = reports.filter(r => selected.includes(r.id));

      const response = await axios.post(
        '/powerbi/export-excel',
        { reports: selectedReports },
        {
          responseType: 'blob',
          skipErrorInterceptor: true,
          validateStatus: () => true,
        }
      );

      if (response.status !== 200) {
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          try {
            const errData = JSON.parse(text);
            throw new Error(errData.message || `Export failed (${response.status})`);
          } catch (parseErr) {
            throw new Error(`Export failed (${response.status})`);
          }
        }
        throw new Error(`Export failed (${response.status})`);
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'mosymphony-analytics.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose();
      if (onShowToast) onShowToast('Excel file downloaded successfully', 'success');
    } catch (err) {
      setError(err.message || 'Export failed. Please try again.');
    }
    setDownloading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box download-modal">
        <div className="download-modal__header">
          <span className="download-modal__title">Download Plots</span>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <p className="download-modal__subtitle">Select plots to download as Excel</p>
        <div className="download-modal__list">
          {reports.map(r => (
            <label key={r.id} className="download-modal__item">
              <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
              <span>{r.title}</span>
            </label>
          ))}
        </div>
        {error && <p className="download-modal__error">{error}</p>}
        <div className="download-modal__actions">
          <button className="btn btn--outline" onClick={onClose} disabled={downloading}>Cancel</button>
          <button className="btn btn--dark" onClick={handleDownload}
            disabled={downloading || selected.length === 0}>
            {downloading ? 'Downloading...' : `Download Excel (${selected.length})`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Filter builder — per-plot aware ─────────────────────
const buildFilters = (settings, plotType) => {
  const filters = [];

  // Custom plots can be built on any table in the workspace dataset.
  // Fan filters out across ALL known tables so at least one will match —
  // Power BI silently ignores filters that target tables/columns not in the visual.
  const targets = plotType === 'custom'
    ? ALL_FILTER_TARGETS
    : [PLOT_FILTER_TARGETS[plotType] || PLOT_FILTER_TARGETS.otif];

  const range = getDurationRange(settings.duration);

  for (const target of targets) {
    if (settings.cmo && settings.cmo !== 'All') {
      filters.push({
        $schema: 'http://powerbi.com/product/schema#basic',
        target: { table: target.table, column: target.cmoCol },
        operator: 'In',
        values: [settings.cmo],
        filterType: 1,
      });
    }

    if (settings.brand && settings.brand !== 'All') {
      filters.push({
        $schema: 'http://powerbi.com/product/schema#basic',
        target: { table: target.table, column: target.brandCol },
        operator: 'In',
        values: [settings.brand],
        filterType: 1,
      });
    }

    if (settings.productType && settings.productType !== 'Multiple Selection') {
      filters.push({
        $schema: 'http://powerbi.com/product/schema#basic',
        target: { table: target.table, column: target.productCol },
        operator: 'In',
        values: [settings.productType],
        filterType: 1,
      });
    }

    if (range) {
      filters.push({
        $schema: 'http://powerbi.com/product/schema#advanced',
        target: { table: target.table, column: target.dateCol },
        logicalOperator: 'And',
        conditions: [
          { operator: 'GreaterThanOrEqual', value: range.from.toISOString() },
          { operator: 'LessThanOrEqual', value: range.to.toISOString() },
        ],
        filterType: 0,
      });
    }
  }

  return filters;
};

// ── Main Dashboard ──────────────────────────────────────
const PODashboard = () => {
  // const [filterText, setFilterText] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [successModal, setSuccessModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [oorChecked, setOorChecked] = useState({});
  const [selectedPlotId, setSelectedPlotId] = useState(null);
  const [plotSettings, setPlotSettings] = useState({});
  const [plotSettingsMap, setPlotSettingsMap] = useState({});
  const [filterValues, setFilterValues] = useState({});
  const [customPlots, setCustomPlots] = useState([]);
  const [showCreateMode, setShowCreateMode] = useState(false);
  const [editingPlot, setEditingPlot] = useState(null);
  const [renamingPlot, setRenamingPlot] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [refreshKeys, setRefreshKeys] = useState({});

  const oorRef = useRef(null);
  const createModeRef = useRef(null);
  const editModeRef = useRef(null);
  const reportInstancesRef = useRef({});

  const allReports = useMemo(() => [...DEFAULT_REPORTS, ...customPlots], [customPlots]);

  const activeFilters = useMemo(() => {
    if (!selectedPlotId) return null;
    const report = allReports.find(r => r.id === selectedPlotId);
    if (!report) return null;
    return buildFilters(plotSettingsMap[selectedPlotId] || {}, report.plotType);
  }, [selectedPlotId, plotSettingsMap, allReports]);

  const activeDuration = useMemo(() => {
    if (!selectedPlotId) return 'all';
    return (plotSettingsMap[selectedPlotId] || {}).duration || 'all';
  }, [selectedPlotId, plotSettingsMap]);

  // Raw side-panel settings for the selected plot. Used to drive slicer state
  // on custom plots (which filter via the template's hidden slicers, not via
  // report-level setFilters()).
  const activeSettings = useMemo(() => {
    if (!selectedPlotId) return null;
    return plotSettingsMap[selectedPlotId] || null;
  }, [selectedPlotId, plotSettingsMap]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    const loadFilterValues = async () => {
      try {
        const res = await axios.get(`/powerbi/filter-values/${DEFAULT_REPORTS[0].id}`);
        if (res.data?.status && res.data?.data) setFilterValues(res.data.data);
      } catch (err) { console.warn('Could not load filter values:', err); }
    };
    const loadCustomPlots = async () => {
      try {
        const res = await axios.get('/powerbi/custom-plots/list');
        if (res.data?.status && Array.isArray(res.data.data)) {
          const plots = res.data.data.map(p => ({
            id: p.report_id,
            plotId: p.plot_id,
            title: p.plot_name || 'Custom Plot',
            plotType: 'custom',
            hasOutOfRange: false,
            isCustom: true,
          }));
          setCustomPlots(plots);
        }
      } catch (err) { console.warn('Could not load custom plots:', err); }
    };
    loadFilterValues();
    loadCustomPlots();
  }, []);

  const handleReportLoadedWithFilters = (reportId, reportInstance) => {
    reportInstancesRef.current[reportId] = reportInstance;
  };

  const handleOorCheck = (reportId, checked) => {
    setOorChecked(p => ({ ...p, [reportId]: checked }));
    if (checked) {
      setTimeout(() => oorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  };

  const handleSetPlotSettings = (newSettings) => {
    setPlotSettings(newSettings);
    if (selectedPlotId) {
      setPlotSettingsMap(prev => ({ ...prev, [selectedPlotId]: newSettings }));
    }
  };

  const handleSelectPlot = (plotId) => {
    setSelectedPlotId(plotId);
    const report = allReports.find(r => r.id === plotId);
    if (report) {
      const existing = plotSettingsMap[plotId] || {
        xAxis: 'Duration',
        yAxis: YAXIS_OPTIONS_MAP[report.plotType]?.[0]?.value || '',
        productType: 'Multiple Selection',
        brand: 'All',
        cmo: 'All',
        duration: 'all',
      };
      setPlotSettings(existing);
    }
  };

  // Opens the Power BI editor on a cloned template report. CreateModePlot
  // handles the clone internally on mount, so the user sees Power BI directly
  // after clicking — name input and Save Plot appear at the bottom (old UX).
  const handleAddNewPlots = () => {
    setShowCreateMode(true);
    setEditingPlot(null);
    setSelectedPlotId(null);
    setTimeout(() => {
      createModeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const handleCancelCreate = () => setShowCreateMode(false);

  const handlePlotSaved = (newPlot) => {
    const plotNumber = DEFAULT_REPORTS.length + customPlots.length + 1;
    setCustomPlots(prev => [...prev, {
      id: newPlot.id,
      plotId: newPlot.plotId,
      title: newPlot.title,
      plotType: 'custom',
      hasOutOfRange: false,
      isCustom: true,
    }]);
    setShowCreateMode(false);
    setSuccessModal(`Plot ${plotNumber}: ${newPlot.title} saved successfully`);
  };

  const handleRequestEdit = (plot) => {
    setEditingPlot(plot);
    setShowCreateMode(false);
    setSelectedPlotId(null);
    setTimeout(() => {
      editModeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  };

  const handleCancelEdit = () => setEditingPlot(null);

  const handleEditSaved = (editedPlot) => {
    setEditingPlot(null);
    setRefreshKeys(prev => ({ ...prev, [editedPlot.id]: (prev[editedPlot.id] || 0) + 1 }));
    showToast(`"${editedPlot.title}" updated successfully`, 'success');
  };

  const handleRequestRename = (plot) => {
    setRenamingPlot(plot);
  };

  const handleConfirmRename = async (newName) => {
    if (!renamingPlot) return;
    const { plotId, id } = renamingPlot;
    setRenamingPlot(null);
    try {
      const res = await axios.put(`/powerbi/custom-plots/${plotId || id}/rename`, { plotName: newName });
      if (res.data?.status) {
        setCustomPlots(prev => prev.map(p =>
          p.id === id ? { ...p, title: newName } : p
        ));
        showToast(`Renamed to "${newName}"`, 'success');
      } else {
        showToast(res.data?.message || 'Failed to rename', 'error');
      }
    } catch (err) {
      console.error('Rename error:', err);
      showToast(err.response?.data?.message || 'Failed to rename plot', 'error');
    }
  };

  const handleRequestDelete = (plot) => {
    setConfirmDelete({ id: plot.id, plotId: plot.plotId, title: plot.title });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { id, plotId, title } = confirmDelete;
    setConfirmDelete(null);
    try {
      await axios.delete(`/powerbi/custom-plots/${plotId || id}`);
      setCustomPlots(prev => prev.filter(p => p.id !== id));
      if (selectedPlotId === id) setSelectedPlotId(null);
      showToast(`"${title}" deleted`, 'success');
    } catch (err) {
      console.error('Delete plot error:', err);
      showToast('Failed to delete plot', 'error');
    }
  };

  const handleDownloadPlots = () => setShowDownloadModal(true);

  const handleApplyFilter = async () => {
    // Apply product code filter via slicer on all plots — does NOT disturb CMO/Brand/Duration slicers
    for (const reportId of Object.keys(reportInstancesRef.current)) {
      const reportInstance = reportInstancesRef.current[reportId];
      if (!reportInstance) continue;
      try {
        // Find the product_code hidden slicer in this report
        const pages = await reportInstance.getPages();
        let productSlicer = null;
        for (const page of pages) {
          try {
            const visuals = await page.getVisuals();
            for (const slicer of visuals.filter(v => v.type === 'slicer')) {
              try {
                const state = await slicer.getSlicerState();
                const col = (state?.targets?.[0]?.column || '').toLowerCase();
                if (col === 'product_code' || col === 'fgmaterialcode' || col === 'product code') {
                  productSlicer = slicer;
                  break;
                }
              } catch {}
            }
          } catch {}
          if (productSlicer) break;
        }

        if (productSlicer) {
          if (selectedProduct) {
            const slicerTarget = (await productSlicer.getSlicerState()).targets[0];
            await productSlicer.setSlicerState({
              filters: [{
                $schema: 'http://powerbi.com/product/schema#basic',
                target: slicerTarget,
                operator: 'In',
                values: [selectedProduct.code],
                filterType: 1,
              }],
            });
          } else {
            await productSlicer.setSlicerState({ filters: [] });
          }
        }
      } catch (err) {
        console.error('[PowerBI] product slicer error', reportId, err);
      }
    }
  };

  const selectedReport = selectedPlotId ? allReports.find(r => r.id === selectedPlotId) : null;

  let tc = 0;
  const tableNumbers = {};
  allReports.forEach(r => { if (r.hasOutOfRange) { tc++; tableNumbers[r.id] = tc; } });
  const hasAnyOorVisible = allReports.some(r => r.hasOutOfRange && oorChecked[r.id]);

  const isAnyModeActive = showCreateMode || editingPlot;

  return (
    <div className="analytics-container">
      <div className="analytics-topbar">
        <div className="analytics-topbar__search">
          <div className="analytics-topbar__search-field">
            <svg className="analytics-topbar__search-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type="text"
                placeholder="Sponsor Product Code / Description"
                value={productSearch}
                onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true); if (!e.target.value) setSelectedProduct(null); }}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                className="analytics-topbar__input"
                style={{ width: '100%' }}
              />
              {showProductDropdown && filterValues?.products?.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                  background: '#fff', border: '1px solid #ddd', borderRadius: 4,
                  maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}>
                  <div style={{ padding: '8px 12px', cursor: 'pointer', color: '#666', fontStyle: 'italic' }}
                    onMouseDown={() => { setSelectedProduct(null); setProductSearch(''); setShowProductDropdown(false); }}>
                    All Products (clear filter)
                  </div>
                  {filterValues.products
                    .filter(p => !productSearch || p.label.toLowerCase().includes(productSearch.toLowerCase()))
                    .map(p => (
                      <div key={p.code}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                          background: selectedProduct?.code === p.code ? '#f0f4ff' : 'transparent' }}
                        onMouseDown={() => { setSelectedProduct(p); setProductSearch(p.label); setShowProductDropdown(false); }}>
                        {p.label}
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
          <button className="btn btn--orange" onClick={handleApplyFilter}>Apply Filter</button>
        </div>
        <div className="analytics-topbar__actions">
          <button className="btn btn--outline" onClick={handleDownloadPlots}>Download Plots</button>
          <button className="btn btn--outline" onClick={handleAddNewPlots} disabled={isAnyModeActive}>
            Add New Plots
          </button>
        </div>
      </div>

      <div className="analytics-main">
        <div className="plots-workspace">
          <div className="plots-grid">
            {DEFAULT_REPORTS.map((report) => (
              <PlotCard
                key={report.id}
                report={report}
                checked={!!oorChecked[report.id]}
                onCheckChange={checked => handleOorCheck(report.id, checked)}
                isSelected={selectedPlotId === report.id}
                onSelect={handleSelectPlot}
                onReportLoaded={handleReportLoadedWithFilters}
                activeFilters={selectedPlotId === report.id ? activeFilters : null}
                activeSettings={selectedPlotId === report.id ? activeSettings : null}
                activeDuration={selectedPlotId === report.id ? activeDuration : 'all'}
                selectedProduct={selectedProduct}
              />
            ))}
          </div>

          {customPlots.length > 0 && (
            <>
              <div className="custom-plots-divider"><span>My Custom Plots</span></div>
              <div className="plots-grid plots-grid--custom">
                {customPlots.map((plot) => (
                  <PlotCard
                    key={plot.id}
                    report={plot}
                    isCustom={true}
                    isSelected={selectedPlotId === plot.id}
                    onSelect={handleSelectPlot}
                    onReportLoaded={handleReportLoadedWithFilters}
                    activeFilters={selectedPlotId === plot.id ? activeFilters : null}
                    activeSettings={selectedPlotId === plot.id ? activeSettings : null}
                    activeDuration={selectedPlotId === plot.id ? activeDuration : 'all'}
                    selectedProduct={selectedProduct}
                    onEdit={handleRequestEdit}
                    onRename={handleRequestRename}
                    onDelete={handleRequestDelete}
                    refreshKey={refreshKeys[plot.id] || 0}
                  />
                ))}
              </div>
            </>
          )}

          {showCreateMode && (
            <div ref={createModeRef}>
              <CreateModePlot
                onCancel={handleCancelCreate}
                onSaved={handlePlotSaved}
              />
            </div>
          )}

          {editingPlot && (
            <div ref={editModeRef}>
              <EditModePlot
                plotToEdit={editingPlot}
                onCancel={handleCancelEdit}
                onSaved={handleEditSaved}
              />
            </div>
          )}
        </div>

        {!isAnyModeActive && (
          <SidePanel
            selectedReport={selectedReport}
            filterValues={filterValues}
            plotSettings={plotSettings}
            setPlotSettings={handleSetPlotSettings}
          />
        )}
      </div>

      {hasAnyOorVisible && (
        <div className="oor-section" ref={oorRef}>
          {allReports.filter(r => r.hasOutOfRange && oorChecked[r.id]).map(report => (
            <OORTable
              key={report.id}
              report={report}
              tableNumber={tableNumbers[report.id]}
              visible={true}
            />
          ))}
        </div>
      )}

      {showDownloadModal && (
        <DownloadModal
          reports={allReports}
          onClose={() => setShowDownloadModal(false)}
          onShowToast={showToast}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          message={`Are you sure you want to delete "${confirmDelete.title}"?`}
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {renamingPlot && (
        <RenameModal
          currentName={renamingPlot.title}
          onConfirm={handleConfirmRename}
          onCancel={() => setRenamingPlot(null)}
        />
      )}

      {successModal && (
        <SuccessModal
          message={successModal}
          onClose={() => setSuccessModal(null)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default PODashboard;
